import { createApiClientWithAuth } from '@/libs/api/base/api-client.ts'
import type {
  ApiClientOptions,
  ApiClientWithAuth,
  ResponseWrapper
} from '@/libs/api/base/api.types.ts'
import type {
  DeviceClassResponse,
  ProductOfferResponse,
  ProductSpecificationRequest,
  ProductSpecificationResponse
} from '@/libs/api/insurance/insurance.types.ts'
import type { SupportedLocales } from '@/ts/types/locale.types'
import { formatDate } from '@/utils/date-util.ts'

export type InsuranceApiClientType = {
  getDeviceClasses(): Promise<DeviceClassResponse>
  getDeviceClassOffer(deviceClassId: string, isCommercial: 0 | 1): Promise<ProductOfferResponse[]>
  getDeviceClassOffers(
    deviceClassIds: string[],
    isCommercial: 0 | 1
  ): Promise<ProductOfferResponse[]>
  getProductSpecifications(
    params: ProductSpecificationRequest
  ): Promise<ResponseWrapper<ProductSpecificationResponse>>
} & ApiClientWithAuth

export const useInsuranceApiClient = (
  locale: SupportedLocales,
  options?: Partial<ApiClientOptions>
): InsuranceApiClientType => {
  const auth = insuranceApiAuthFactory(locale)
  const defaultOptions = {
    key: 'insurance',
    prefixUrl: import.meta.env.VITE_INSURANCE_BASE_URL,
    auth
  }

  const client = createApiClientWithAuth({
    ...defaultOptions,
    ...options
  })

  return {
    ...client,

    async getDeviceClasses(): Promise<DeviceClassResponse> {
      const response = await client.get<ResponseWrapper<DeviceClassResponse>>('api/device-classes')
      return (response.data as DeviceClassResponse[])[0]
    },

    async getDeviceClassOffer(
      deviceClassId: string,
      isCommercial: 0 | 1
    ): Promise<ProductOfferResponse[]> {
      const response = await client.get<ResponseWrapper<ProductOfferResponse>>(
        'api/products/offers',
        {
          device_class_id: deviceClassId,
          is_commercial: isCommercial.toString()
        }
      )
      return response.data as ProductOfferResponse[]
    },

    async getDeviceClassOffers(
      deviceClassIds: string[],
      isCommercial: 0 | 1
    ): Promise<ProductOfferResponse[]> {
      const params = new URLSearchParams()
      deviceClassIds.forEach((id) => {
        params.append('device_class_id[]', id)
      })
      params.append('is_commercial', isCommercial.toString())

      const response = await client.get<ResponseWrapper<ProductOfferResponse[]>>(
        'api/products/offers',
        params
      )
      return response.data as ProductOfferResponse[]
    },

    getProductSpecifications(params: ProductSpecificationRequest) {
      return client.get<ResponseWrapper<ProductSpecificationResponse>>('api/products/specs', {
        product_id: params.product_id,
        device_class_id: params.device_class_id,
        purchase_date: formatDate(params.purchase_date),
        purchase_price: params.purchase_price.toString(),
        is_refurbished: params.is_refurbished.toString(),
        is_commercial: params.is_refurbished.toString()
      })
    }
  }
}

function insuranceApiAuthFactory(locale: SupportedLocales): {
  client_id: string
  client_secret: string
} {
  switch (locale) {
    case 'de-AT':
      return {
        client_id: import.meta.env.VITE_INSURANCE_CLIENT_de_AT,
        client_secret: import.meta.env.VITE_INSURANCE_SECRET_de_AT
      }
    default:
      return {
        client_id: import.meta.env.VITE_INSURANCE_CLIENT_de_DE,
        client_secret: import.meta.env.VITE_INSURANCE_SECRET_de_DE
      }
  }
}
